import { formatCalendarDate } from './formatCalendarDate';

/**
 * One place for how dates, sizes and money read across the app.
 * Timecodes live in ./timecode (formatTimecode).
 */

const MB = 1024 * 1024;
const GB = 1024 * MB;

/** "Oct 31, 2026". Date-only strings keep their calendar date in every timezone. */
export const formatDate = (value: string | number | Date | null | undefined): string =>
  formatCalendarDate(value);

/** "Oct 31, 2026, 2:05 PM" in the viewer's timezone. */
export const formatDateTime = (value: string | number | Date | null | undefined): string =>
  formatCalendarDate(value, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/**
 * "just now", "5 min ago", "3 h ago", "yesterday", "4 days ago", then a date.
 * Lowercase, because it always follows other words ("Updated 3 h ago").
 * For activity feeds only; everywhere else use formatDate.
 */
export const formatRelativeTime = (value: string | number | Date | null | undefined, now: Date = new Date()): string => {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
  if (seconds < 0) return formatDate(date);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return formatDate(date);
};

/**
 * File sizes and storage in the same binary units the plans use
 * ("1 GB" = 1024^3 bytes): "740 KB", "173.6 MB", "4.2 GB", "10 GB".
 * -1 (or any negative) reads as "Unlimited".
 */
export const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '—';
  if (bytes < 0) return 'Unlimited';
  if (bytes >= GB) return `${Number((bytes / GB).toFixed(1))} GB`;
  if (bytes >= MB) return `${Number((bytes / MB).toFixed(1))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

/** "CA$14.99" or "US$14.99": the currency is always visible, since plans are billed in CAD or USD. */
export const formatMoney = (amount: number, currency: string = 'CAD'): string =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency.toUpperCase(), currencyDisplay: 'symbol' })
    .format(amount)
    // en-CA renders CAD as "$"; spell the currency when it isn't obvious.
    .replace(/^\$/, currency.toUpperCase() === 'CAD' ? 'CA$' : '$');

/** "1 project", "3 projects". */
export const pluralize = (count: number, singular: string, plural: string = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;
